import { z } from 'zod';
import { ConfigKind, ScopeLevel } from './config.js';
import { CLIENT_LIFECYCLE } from './clients.js';
import { Industry } from './controls.js';
import { listQuery } from './list.js';
import { PLACEMENT_STATUSES } from './placements.js';
import { ASSESSMENT_STATUSES, } from './portfolio.js';
import { ReleaseStatus } from './publishing.js';
import { SCORE_BANDS } from './scoring.js';
/**
 * The lists of the app and what each can be searched, filtered and sorted by (task 60). The server checks
 * the query with these schemas; the web app builds its filters and sort menus from the same names.
 */
const Status = z.enum(ASSESSMENT_STATUSES);
const Band = z.enum(SCORE_BANDS.map((b) => b.key));
// ---------- Clients: the businesses a tenant assesses ----------
export const ClientsQuery = listQuery({
    sorts: ['updated', 'name', 'score', 'progress', 'assessments', 'renewal', 'premium'],
    filters: {
        /** Active clients, or the archive: lapsed ones (task 66). */
        tab: z.enum(['active', 'archived']).default('active'),
        lifecycle: z.enum(CLIENT_LIFECYCLE).optional(),
        /** Renewal date passed, or within this many days. */
        renewing: z.enum(['overdue', '30', '60', '90']).optional(),
        /** Of the client's latest assessment. */
        status: Status.optional(),
        band: Band.optional(),
        industry: Industry.optional(),
        /** `to_review`: the latest assessment waits on the insurer's review or decision (task 109). */
        attention: z.enum(['exceptions', 'provisional', 'unscored', 'to_review']).optional(),
        /** The latest assessment has a placement at this stage with some insurer (task 93). */
        placement: z.enum(PLACEMENT_STATUSES).optional(),
    },
});
// ---------- Assessments ----------
export const AssessmentsQuery = listQuery({
    sorts: ['updated', 'created', 'title', 'business', 'score'],
    filters: {
        status: Status.optional(),
        band: Band.optional(),
        industry: Industry.optional(),
        /** One client's assessments (its customer key). */
        customer: z.string().trim().min(1).max(200).optional(),
    },
});
// ---------- Portfolio ----------
export const PortfolioQuery = listQuery({
    sorts: ['updated', 'score', 'change', 'evidence', 'loss', 'name'],
    filters: {
        status: Status.optional(),
        band: Band.optional(),
        industry: Industry.optional(),
        exceptions: z.enum(['yes', 'no']).optional(),
    },
});
// ---------- People and organisations ----------
export const MembersQuery = listQuery({
    sorts: ['email', 'name', 'last_seen', 'joined'],
    dir: 'asc',
    filters: {
        status: z.enum(['active', 'suspended']).optional(),
        role: z
            .string()
            .regex(/^([a-z_]{3,30}|c_[a-z0-9]{12})$/)
            .optional(),
    },
});
export const OrganisationsQuery = listQuery({
    sorts: ['created', 'name', 'members'],
    filters: { kind: z.enum(['insurer', 'broker']).optional() },
});
// ---------- Configuration and re-scores ----------
export const ConfigItemsQuery = listQuery({
    sorts: ['key', 'title', 'updated'],
    dir: 'asc',
    filters: {
        kind: ConfigKind.optional(),
        scope: ScopeLevel.optional(),
        retired: z.enum(['yes', 'no']).optional(),
    },
});
export const ReleasesQuery = listQuery({ sorts: ['created'], filters: { status: ReleaseStatus.optional() } });
export const RescoresQuery = listQuery({
    sorts: ['requested'],
    filters: {
        status: z.enum(['pending', 'applied', 'rejected', 'withdrawn']).optional(),
        /** Everything but the open proposal. */
        decided: z.enum(['yes']).optional(),
    },
});
//# sourceMappingURL=lists.js.map